import { Link } from 'react-router-dom';
import usePageMeta from '../../hooks/usePageMeta';
import { Users, Briefcase, TrendingUp, ArrowRight, Target, Clock } from 'lucide-react';

export default function StaffingServicesPage() {
  usePageMeta({
    title: 'Staffing Services – Stratel',
    description: 'Discover Stratel’s flexible staffing solutions tailored for long-term care organizations and healthcare providers.',
    keywords: 'staffing services, healthcare staffing, long-term care staffing, workforce solutions',
  });
  const benefits = [
    {
      icon: Users,
      title: 'Vetted Professionals',
      description: 'Access our network of qualified healthcare professionals and consultants thoroughly vetted for expertise and fit.',
    },
    {
      icon: Briefcase,
      title: 'Flexible Engagement',
      description: 'From short-term projects to long-term placements, we customize staffing solutions to match your needs.',
    },
    {
      icon: TrendingUp,
      title: 'Designed for Progress',
      description: 'Our staffing solutions are designed to support measurable progress and stronger organizational performance.',
    },
    {
      icon: Clock,
      title: 'Fast Turnaround',
      description: 'Quick matching and placement process to get your team staffed when you need them.',
    },
  ];

const staffingTypes = [
    'Registered Nurse (RN)',
    'Licensed Practical Nurse (LPN) / Licensed Vocational Nurse (LVN)',
    'Certified Nursing Assistant (CNA)',
    'Medication Technician',
    // 'Director of Nursing (DON)',
    // 'Nurse Practitioner (NP)',
    // 'Long-Term Care Administrator',
    // 'Social Worker (MSW)',
    // 'Activities Coordinator',
    // 'Physical Therapist (PT)',
    // 'Occupational Therapist (OT)',
    // 'Speech-Language Pathologist (SLP)',
    'Dietary Staff',
    'Other'
    ];
    
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-emerald-600 via-emerald-500 to-emerald-700 text-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-emerald-500/30 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <span className="text-sm font-semibold text-emerald-100">Expert Talent Solutions for Organizations</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Find the Right Talent, Faster
              </h1>
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                Access our network of healthcare professionals and consultants who understand your industry. We handle recruitment, vetting, and placement so you can focus on your mission.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center bg-white text-emerald-700 px-8 py-4 rounded-lg font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Request Staffing
                  <ArrowRight size={20} className="ml-2" />
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="space-y-6">
                  <div>
                    <p className="text-emerald-100 mb-4 italic">
                      "We partner with healthcare organizations to source and match qualified professionals quickly and reliably."
                    </p>
                    <p className="text-emerald-200 font-semibold">— Stratel Staffing</p>
                  </div>
                  <div className="border-t border-white/20 pt-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-emerald-200" />
                      <span className="text-emerald-100">Curated network of vetted professionals</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-emerald-200" />
                      <span className="text-emerald-100">Typical candidate presentation: Within days</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-secondary-900 mb-16">
          Why Partner With Stratel for Staffing?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {benefits.map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <div key={idx} className="bg-gradient-to-br from-white to-secondary-50 rounded-xl shadow-md overflow-hidden border border-secondary-100 p-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-14 w-14 rounded-lg bg-emerald-100">
                      <Icon className="h-7 w-7 text-emerald-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-secondary-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-secondary-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Staffing Categories */}
      <div className="bg-gradient-to-br from-emerald-50 to-secondary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 text-center mb-4">
            Staffing Categories
          </h2>
          <p className="text-xl text-secondary-600 text-center mb-16 max-w-2xl mx-auto">
            Specialized talent across healthcare consulting, operations, and administration
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {staffingTypes.map((type, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-md p-6 border border-secondary-100 flex items-center gap-4">
                <div className="flex-shrink-0 w-3 h-3 bg-emerald-600 rounded-full"></div>
                <span className="text-secondary-700 font-semibold text-lg">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Process */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 text-center mb-16">
          Our Staffing Process
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        {[
  { step: 1, title: 'Request Needs', desc: 'Request your specific role requirements, shift patterns, and skill priorities.' },
  { step: 2, title: 'Match Candidates', desc: 'Rapidly match and present the best-fit professionals tailored to your request.' },
  { step: 3, title: 'Vet & Confirm', desc: 'Fast screening, credential checks, and confirmation of availability.' },
  { step: 4, title: 'Place & Support', desc: 'Coordinate onboarding, scheduling, and provide ongoing support.' },
].map((item, idx) => (
            <div key={idx} className="relative">
              {idx < 3 && (
                <div className="hidden md:block absolute top-12 left-full w-8 h-0.5 bg-gradient-to-r from-emerald-500 to-emerald-300 -translate-x-4"></div>
              )}
              <div className="bg-white rounded-xl shadow-md p-8 border border-secondary-100 text-center relative z-10">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-secondary-900 mb-2 text-lg">{item.title}</h3>
                <p className="text-sm text-secondary-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Engagement Options */}
      <div className="bg-gradient-to-br from-secondary-900 to-secondary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Flexible Engagement Models
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Project-Based',
                description: 'Short-term specialized talent for specific initiatives and projects.',
                duration: '3-6 months'
              },
              {
                title: 'Contract Staffing',
                description: 'Flexible professionals for temporary needs with option to convert.',
                duration: '6-12 months'
              },
              {
                title: 'Temporary Placement',
                description: 'Short to mid-term placements with comprehensive vetting and onboarding.',
                duration: 'Up to 12 months'
              },
            ].map((model, idx) => (
              <div key={idx} className="bg-secondary-800/50 backdrop-blur-sm border border-secondary-700 rounded-xl p-8 transition-colors duration-300">
                <h3 className="text-2xl font-bold mb-3">{model.title}</h3>
                <p className="text-secondary-300 mb-4 leading-relaxed">{model.description}</p>
                <div className="pt-4 border-t border-secondary-700">
                  <span className="text-emerald-300 font-semibold">{model.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-12 md:p-16 text-center text-white shadow-xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Strengthen Your Team?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto leading-relaxed">
            Tell us about your staffing needs and let's find the right professional for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/get-started"
              className="inline-flex items-center justify-center bg-white text-emerald-700 px-8 py-4 rounded-lg font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request Staffing
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
